import { useEffect, useMemo, useState } from "react";
import type { ReportDefinition, ReportOption, ReportSection, SectionResult } from "../../../api/portalReportsApi";
import { ErrorTile } from "../ReportBits";
import { ChartSection } from "./ChartSection";
import { initialValues, ReportInputsForm } from "./ReportInputsForm";
import { applyFilters, SectionFilters, type FilterState } from "./SectionFilters";
import { StatsSection } from "./StatsSection";
import { TableSection } from "./TableSection";

const ERROR_TEXT: Record<string, string> = {
  section_failed: "This section could not be loaded.",
  procedure_not_allowed: "This section is not configured correctly.",
};

function errorText(code: string): string {
  if (ERROR_TEXT[code]) return ERROR_TEXT[code];
  if (code.startsWith("input_required:")) return `"${code.slice(15)}" is required.`;
  if (code.startsWith("invalid_value:")) return `A value has the wrong format (${code.slice(14)}).`;
  return code;
}

/**
 * Runtime of a dynamic report: the input form, one run, and the sections
 * rendered from the definition. Used by the customer page and by the admin
 * preview (which passes its own `run`).
 */
export function DynamicReport({
  definition,
  run,
  loadOptions,
  exportName,
}: {
  definition: ReportDefinition;
  run: (inputs: Record<string, string>) => Promise<SectionResult[]>;
  loadOptions?: (key: string) => Promise<ReportOption[]>;
  exportName: string;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(definition.inputs));
  const [results, setResults] = useState<SectionResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setLoading(true);
    setError(null);
    try {
      setResults(await run(values));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run the report");
    } finally {
      setLoading(false);
    }
  };

  // Reports without inputs (or with defaults for all of them) can open with data.
  useEffect(() => {
    const canAuto = definition.inputs.length === 0 || definition.autoRun;
    if (canAuto && results == null && !loading) void go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);

  return (
    <>
      {definition.inputs.length > 0 && (
        <ReportInputsForm
          inputs={definition.inputs}
          values={values}
          onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
          onSubmit={() => void go()}
          loading={loading}
          loadOptions={loadOptions}
        />
      )}
      {error && <ErrorTile message={error} />}
      {results == null && !error && !loading && definition.inputs.length > 0 && (
        <div className="tile" style={{ color: "var(--portal-muted)" }}>Choose the filters and press Go to load the report.</div>
      )}
      {loading && results == null && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
      {results && (
        <div style={{ display: "grid", gap: 16 }}>
          {definition.sections.map((section) => (
            <SectionTile key={section.id} section={section} result={results.find((r) => r.sectionId === section.id)} exportName={`${exportName}-${section.title || section.id}`} />
          ))}
        </div>
      )}
    </>
  );
}

function SectionTile({ section, result, exportName }: { section: ReportSection; result: SectionResult | undefined; exportName: string }) {
  const [filters, setFilters] = useState<FilterState>({});
  const colIndex = useMemo(() => {
    const idx: Record<string, number> = {};
    (result?.columns ?? []).forEach((c, i) => {
      idx[c.name] = i;
    });
    return idx;
  }, [result]);
  const rows = result?.rows ?? [];
  const filtered = useMemo(() => applyFilters(rows, colIndex, filters), [rows, colIndex, filters]);
  const filterColumns = section.filterColumns ?? [];

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>{section.title || section.id}</h3>
        {result?.truncated && (
          <span style={{ fontSize: "0.8rem", color: "#8a5a00", background: "#fff4e0", border: "1px solid #f0c36d", borderRadius: 999, padding: "2px 8px" }}>
            Showing the first {rows.length.toLocaleString("en-GB")} rows
          </span>
        )}
      </header>
      {!result ? (
        <div style={{ color: "var(--portal-muted)" }}>No data.</div>
      ) : result.error ? (
        <div style={{ color: "#c0392b" }}>{errorText(result.error)}</div>
      ) : (
        <>
          {filterColumns.length > 0 && (
            <SectionFilters filterColumns={filterColumns} columns={section.columns} colIndex={colIndex} rows={rows} filters={filters} onChange={setFilters} />
          )}
          {section.kind === "chart" && section.chart ? (
            <ChartSection chart={section.chart} columns={section.columns} colIndex={colIndex} rows={filtered} />
          ) : section.kind === "stats" ? (
            <StatsSection columns={section.columns} colIndex={colIndex} rows={filtered} />
          ) : (
            <TableSection title={section.title || section.id} columns={section.columns} colIndex={colIndex} rows={filtered} pageSize={section.pageSize ?? 100} exportName={exportName} />
          )}
        </>
      )}
    </section>
  );
}
